import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';
import Paragraph from 'packages/kakapo-ui-kit/components/common/Paragraph';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import PrimaryButton from 'packages/kakapo-ui-kit/components/buttons/PrimaryButton';
import PlainButton from 'packages/kakapo-ui-kit/components/buttons/PlainButton';
import { ButtonSize } from 'packages/kakapo-ui-kit/types/Button';
import Label from 'packages/kakapo-ui-kit/components/forms/controls/Label';
import { SectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';

import EvaluationSubmissionGraph from './EvaluationSubmissionGraph';
import EvaluationSubmissionScoreModal from './EvaluationSubmissionScoreModal';
import EvaluationSubmissionSection from './EvaluationSubmissionSection';

const Placeholder = styled.div`
	color: ${(props) => props.theme.colors.slateBlue};
	font-size: 16px;
	text-align: center;
`;

export default function EvaluationSubmissionScore(props: {
	submission: graphql.EvaluationSubmission;
	saving?: boolean;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const canOverrideFinalScore = useFeature('2021.evaluations.override');

	const finalScoreItem = props.submission.evaluationForm.sections
		.flatMap((section) => section.items)
		.find((item) => item.type === graphql.EvaluationSectionItemType.FinalScore);

	const [reset, { loading }] = graphql.useOverrideEvaluationSubmissionScoreMutation({
		variables: {
			input: {
				id: props.submission.id,
				competencyLevelID: undefined,
				reason: undefined,
			},
		},
	});

	const [modal, setModal] = useState(false);

	if (!finalScoreItem) {
		return null;
	}

	const isManager =
		props.submission.viewer.party === graphql.EvaluationSubmissionParty.Manager &&
		props.submission.viewer.viewerCanUpdate;

	const showFinalScore =
		(props.submission.manager.hasShared && props.submission.employee.hasShared) || isManager;

	if (!showFinalScore) {
		return null;
	}

	const competencyLevelSet = finalScoreItem.finalScore?.competencyLevelSet;
	const displayLevelSet = finalScoreItem.finalScore?.displayLevelSet;

	if (!competencyLevelSet) {
		return null;
	}

	const levels = displayLevelSet?.levels ?? competencyLevelSet.levels;

	const sections = props.submission.evaluationForm.sections
		.map((section, index) => ({
			name:
				localize(
					section.items.find((item) => item.type === graphql.EvaluationSectionItemType.Title)?.title
						?.text ?? [],
				)?.text ?? `§ ${index + 1}`,
			employee: props.submission.employee.sections[index].score,
			manager: props.submission.manager.sections[index].score,
		}))
		.filter(
			({ name, employee, manager }) =>
				!!name &&
				((employee !== null && employee !== undefined) ||
					(manager !== null && manager !== undefined)),
		)
		.filter(({ manager }) => manager !== null && manager !== undefined);

	if (sections.length === 0) {
		return (
			<EvaluationSubmissionSection mb={4}>
				<Placeholder>{t('evaluation.submission.finalScore.placeholder')}</Placeholder>
			</EvaluationSubmissionSection>
		);
	}

	const finalScore = props.submission.overrideScore ?? props.submission.manager.finalScore;
	const match = finalScore ? levels.findIndex(({ id }) => finalScore.id === id) : undefined;

	if (props.saving) {
		return (
			<EvaluationSubmissionSection mb={4}>
				<Spinner isLoading size="18px" />
			</EvaluationSubmissionSection>
		);
	}

	return (
		<EvaluationSubmissionSection mb={4}>
			{modal && (
				<EvaluationSubmissionScoreModal
					submission={props.submission}
					competencyLevelSet={displayLevelSet ?? competencyLevelSet}
					saving={props.saving ?? false}
					onDismiss={() => setModal(false)}
				/>
			)}
			<EvaluationSubmissionGraph
				levels={levels.map((level) => localize(level.title).text)}
				sections={sections.map((section) => ({
					score: section.manager ?? undefined,
					name: section.name,
				}))}
				match={match}
			/>
			<Flex alignItems="center" mb={3}>
				<SectionTitle color="black">
					{t('evaluation.form.scoreGraph.finalScore.label')}
				</SectionTitle>
				{loading ? (
					<Box ml={3}>
						<Spinner isLoading size="18px" verticalMargin={0} />
					</Box>
				) : (
					<Flex ml={3}>
						{props.submission.manager.viewerCanUpdate && canOverrideFinalScore && (
							<PrimaryButton size={ButtonSize.Small} onClick={() => setModal(true)}>
								{t('evaluation.submission.overrideScore.button.title')}
							</PrimaryButton>
						)}
						{props.submission.overrideScore && (
							<Box ml={2}>
								<PlainButton size={ButtonSize.Small} onClick={() => reset()}>
									{t('evaluation.submission.overrideScore.resetButton.title')}
								</PlainButton>
							</Box>
						)}
					</Flex>
				)}
			</Flex>
			{props.submission.overrideScore ? (
				<Paragraph color="slateBlue">
					<strong>{localize(props.submission.overrideScore.title).text}</strong> (
					{t('evaluation.submission.overrideScore.status.title')})
				</Paragraph>
			) : (
				<Paragraph color="slateBlue">
					<strong>{match !== undefined ? localize(levels[match].title).text : ''}</strong>
				</Paragraph>
			)}
			{props.submission.overrideReason && (
				<Box mt={3}>
					<Label
						spaced={false}
						title={t('evaluation.submission.overrideScore.modal.reason.label')}
					/>
					<Paragraph color="slateBlue">{props.submission.overrideReason}</Paragraph>
				</Box>
			)}
		</EvaluationSubmissionSection>
	);
}
